/**
 * Deep-link resolution (2026-08-23).
 *
 * Notification rows and emails have accumulated FOUR link shapes, and
 * every consumer parsed them differently — so most links quietly landed
 * on the dashboard instead of the thing they named:
 *
 *   https://trailercast.cloud/#/buyer-spaces/<id>/documents?doc=<docId>
 *   #/calls/<id>
 *   /?#demo=<id>&tab=comments
 *   /?demo=<id>
 *   (and NULL, for events that never set one)
 *
 * The failures were specific:
 *   - App's boot router split on [/?] but not '=', so '#demo=<id>' never
 *     matched the phase allowlist and fell through to 'dashboard'.
 *   - The notification panel matched /[?&]demo=/ — which does NOT match
 *     a leading '#demo=', so @mention notifications navigated nowhere.
 *   - parseSpaceHash took parts[2] verbatim, so 'documents?doc=abc'
 *     was not a recognized section and the room opened on Welcome.
 *
 * One parser now serves the boot router, the notification panel, and the
 * room's own hash handling, so a link means the same thing everywhere.
 */

// Phases the app can route to by name. The single source of truth —
// App.jsx's boot router reads it through phaseFromHash below rather
// than keeping its own copy.
const PHASES = new Set([
  'dashboard', 'upload', 'library', 'reports', 'conversations',
  'buyer-spaces', 'calls', 'sales-library', 'qbr', 'coaching',
  'security', 'integrations', 'settings', 'audit-log',
  'forms',
]);

/**
 * The phase segment of a location hash: everything before the first
 * '/' or '?', with the leading '#' (and its optional '/') removed.
 *
 *   '#/buyer-spaces/<id>/documents?doc=x'  ->  'buyer-spaces'
 *   '#/library'                            ->  'library'
 *   '#demo=abc'                            ->  'demo=abc'
 *
 * Exported because App.jsx's phase initializer tests this segment
 * against the PUBLIC phases ('login' / 'signup') too, and those are
 * deliberately not in PHASES — they are auth screens, not deep-link
 * destinations.
 */
export function hashPhaseSegment(hash) {
  return String(hash || '').replace(/^#\/?/, '').split(/[/?]/)[0];
}

/**
 * Resolve a location hash to the app phase it names, or null when it
 * names none.
 *
 * This is the routing half of the parser — "which top-level screen does
 * this URL mean" — and it deliberately ignores the ids parseDeepLink
 * extracts, because each phase component owns its own subpath.
 *
 * It exists because App.jsx's three boot sites (the phase useState
 * initializer, the /auth/me bootstrap, and the post-bootstrap rehydrate
 * effect) each hand-rolled this, and they drifted apart: the bootstrap
 * copy had lost 'buyer-spaces' and 'calls' from its allowlist AND
 * tested the raw hash without stripping the subpath. So a cold load on
 * '#/buyer-spaces/<id>/documents' resolved to 'dashboard', and the
 * phase-hash mirror rewrote the URL to '#/dashboard' before the
 * rehydrate effect could read the deep link (2026-08-24). Three copies
 * of one rule is the bug; one copy is the fix.
 */
export function phaseFromHash(hash) {
  const segment = hashPhaseSegment(hash);
  return PHASES.has(segment) ? segment : null;
}

/**
 * Reduce any of the accepted link shapes to just its hash + query,
 * whether it arrived as an absolute URL, a rooted path, or a bare
 * fragment.
 */
function extractParts(input) {
  if (!input || typeof input !== 'string') return { hash: '', search: '' };
  let rest = input.trim();

  // Absolute URL — drop scheme + host. Deliberately string-based rather
  // than `new URL()`: these values come from the database and a
  // malformed one should degrade to "no deep link", not throw inside a
  // render path.
  const schemeIdx = rest.indexOf('://');
  if (schemeIdx > -1) {
    const afterHost = rest.indexOf('/', schemeIdx + 3);
    rest = afterHost > -1 ? rest.slice(afterHost) : '/';
  }

  const hashIdx = rest.indexOf('#');
  const hash = hashIdx > -1 ? rest.slice(hashIdx + 1) : '';
  const beforeHash = hashIdx > -1 ? rest.slice(0, hashIdx) : rest;
  const qIdx = beforeHash.indexOf('?');
  const search = qIdx > -1 ? beforeHash.slice(qIdx + 1) : '';
  return { hash, search };
}

function paramsOf(str) {
  const out = {};
  if (!str) return out;
  for (const pair of str.split('&')) {
    if (!pair) continue;
    const eq = pair.indexOf('=');
    const k = eq > -1 ? pair.slice(0, eq) : pair;
    const v = eq > -1 ? pair.slice(eq + 1) : '';
    if (!k) continue;
    try { out[decodeURIComponent(k)] = decodeURIComponent(v); }
    catch (_) { out[k] = v; }
  }
  return out;
}

/**
 * Parse a link into an intent.
 *
 * @returns {{
 *   phase: string|null, spaceId: string|null, sectionId: string|null,
 *   docId: string|null, demoId: string|null, callId: string|null,
 *   jobId: string|null, tab: string|null
 * }}
 */
export function parseDeepLink(input) {
  const empty = {
    phase: null, spaceId: null, sectionId: null, docId: null,
    demoId: null, callId: null, jobId: null, tab: null,
  };
  const { hash, search } = extractParts(input);
  if (!hash && !search) return empty;

  // Query params can live before OR after the '#', because the link
  // shapes disagree ('/?demo=x' vs '#demo=x&tab=y'). Read both; the
  // hash wins on conflict since it is the more specific location.
  const searchParams = paramsOf(search);

  const bare = hash.replace(/^\/?/, '');

  // Route form: '/buyer-spaces/<id>/<section>?doc=<docId>'
  if (bare.includes('/') || PHASES.has(bare.split(/[?&]/)[0])) {
    const [pathPart, queryPart] = bare.split('?');
    const hashParams = { ...searchParams, ...paramsOf(queryPart) };
    const segs = pathPart.split('/').filter(Boolean);
    const phase = PHASES.has(segs[0]) ? segs[0] : null;
    if (!phase) return { ...empty, ...pickIds(hashParams) };
    return {
      ...empty,
      ...pickIds(hashParams),
      phase,
      spaceId: phase === 'buyer-spaces' ? (segs[1] || null) : null,
      sectionId: phase === 'buyer-spaces' ? (segs[2] || null) : null,
      callId: phase === 'calls' ? (segs[1] || null) : null,
    };
  }

  // Param form: 'demo=<id>&tab=comments', 'job=<id>'
  const hashParams = { ...searchParams, ...paramsOf(bare) };
  const ids = pickIds(hashParams);
  return {
    ...empty,
    ...ids,
    // A demo link's destination is the completed-trailer view, which
    // App reaches by loading the demo from the dashboard phase — so we
    // report 'dashboard' and let the demo loader take it from there.
    phase: ids.demoId || ids.jobId ? 'dashboard' : null,
  };
}

function pickIds(p) {
  return {
    demoId: p.demo || null,
    jobId: p.job || null,
    docId: p.doc || null,
    tab: p.tab || null,
  };
}

/**
 * Build the in-app hash a parsed intent should live at, so the address
 * bar matches what the user is looking at after we navigate.
 */
export function hashForIntent(intent) {
  if (!intent) return null;
  if (intent.phase === 'buyer-spaces' && intent.spaceId) {
    const base = `#/buyer-spaces/${intent.spaceId}`;
    const withSection = intent.sectionId ? `${base}/${intent.sectionId}` : base;
    return intent.docId ? `${withSection}?doc=${encodeURIComponent(intent.docId)}` : withSection;
  }
  if (intent.phase === 'calls' && intent.callId) return `#/calls/${intent.callId}`;
  if (intent.demoId) {
    return intent.tab
      ? `#demo=${encodeURIComponent(intent.demoId)}&tab=${encodeURIComponent(intent.tab)}`
      : `#demo=${encodeURIComponent(intent.demoId)}`;
  }
  if (intent.jobId) return `#job=${encodeURIComponent(intent.jobId)}`;
  if (intent.phase) return `#/${intent.phase}`;
  return null;
}

export const __TEST_ONLY__ = { extractParts, paramsOf, PHASES };
